export interface RequiredUiBlockQualityObservation {
  overlapCount: number
  clippingCount: number
  unintendedHorizontalOverflow: boolean
  hiddenRequiredActionCount: number
  unresolvedCssVariableCount: number
  invalidComputedColorCount: number
  contrastFailureCount: number
  mobileTouchTargetFailureCount: number
  typographyHierarchyPass: boolean
  emptyVisibleContent: boolean
  contentDensityRatio: number
  unresolvedExpressionCount: number
  score: number
  blockers: string[]
}

export interface RequiredUiBlockQualityMeasurementInput {
  overlapCount: number
  clippingCount: number
  unintendedHorizontalOverflow: boolean
  hiddenRequiredActionCount: number
  unresolvedCssVariableCount: number
  invalidComputedColorCount: number
  contrastFailureCount: number
  mobileTouchTargetFailureCount: number
  typographyHierarchyPass: boolean
  emptyVisibleContent: boolean
  contentDensityRatio: number
  unresolvedExpressionCount: number
}

export interface RequiredUiBlockQualityDomOptions {
  mobile: boolean
  requiredActionElementIds: readonly string[]
}

interface Rgb {
  r: number
  g: number
  b: number
  a: number
}

function nonNegativeInteger(value: number): number {
  return Number.isInteger(value) && value >= 0 ? value : Number.MAX_SAFE_INTEGER
}

export function evaluateRequiredUiBlockQuality(
  input: RequiredUiBlockQualityMeasurementInput,
): RequiredUiBlockQualityObservation {
  const normalized = {
    ...input,
    overlapCount: nonNegativeInteger(input.overlapCount),
    clippingCount: nonNegativeInteger(input.clippingCount),
    hiddenRequiredActionCount: nonNegativeInteger(input.hiddenRequiredActionCount),
    unresolvedCssVariableCount: nonNegativeInteger(input.unresolvedCssVariableCount),
    invalidComputedColorCount: nonNegativeInteger(input.invalidComputedColorCount),
    contrastFailureCount: nonNegativeInteger(input.contrastFailureCount),
    mobileTouchTargetFailureCount: nonNegativeInteger(input.mobileTouchTargetFailureCount),
    contentDensityRatio: Number.isFinite(input.contentDensityRatio)
      ? Math.max(0, Math.min(1, input.contentDensityRatio))
      : 0,
    unresolvedExpressionCount: nonNegativeInteger(input.unresolvedExpressionCount),
  }
  const blockers = [
    ...(normalized.overlapCount === 0 ? [] : [`overlap:${normalized.overlapCount}`]),
    ...(normalized.clippingCount === 0 ? [] : [`clipping:${normalized.clippingCount}`]),
    ...(normalized.unintendedHorizontalOverflow ? ["horizontal-overflow"] : []),
    ...(normalized.hiddenRequiredActionCount === 0 ? [] : [`required-action-hidden:${normalized.hiddenRequiredActionCount}`]),
    ...(normalized.unresolvedCssVariableCount === 0 ? [] : [`css-variable-unresolved:${normalized.unresolvedCssVariableCount}`]),
    ...(normalized.invalidComputedColorCount === 0 ? [] : [`computed-color-invalid:${normalized.invalidComputedColorCount}`]),
    ...(normalized.contrastFailureCount === 0 ? [] : [`contrast:${normalized.contrastFailureCount}`]),
    ...(normalized.mobileTouchTargetFailureCount === 0 ? [] : [`mobile-touch-target:${normalized.mobileTouchTargetFailureCount}`]),
    ...(normalized.typographyHierarchyPass ? [] : ["typography-hierarchy-invalid"]),
    ...(normalized.emptyVisibleContent ? ["empty-visible-content"] : []),
    ...(normalized.contentDensityRatio >= 0.005
      ? []
      : [`content-density-low:${normalized.contentDensityRatio.toFixed(4)}`]),
    ...(normalized.unresolvedExpressionCount === 0
      ? []
      : [`unresolved-expression:${normalized.unresolvedExpressionCount}`]),
  ].sort()
  const penalty = Math.min(100,
    normalized.overlapCount * 6 +
    normalized.clippingCount * 8 +
    (normalized.unintendedHorizontalOverflow ? 15 : 0) +
    normalized.hiddenRequiredActionCount * 15 +
    normalized.unresolvedCssVariableCount * 10 +
    normalized.invalidComputedColorCount * 5 +
    normalized.contrastFailureCount * 8 +
    normalized.mobileTouchTargetFailureCount * 4 +
    (normalized.typographyHierarchyPass ? 0 : 10) +
    (normalized.emptyVisibleContent ? 35 : 0) +
    (normalized.contentDensityRatio >= 0.005 ? 0 : 20) +
    normalized.unresolvedExpressionCount * 15,
  )
  return { ...normalized, score: Math.max(0, 100 - penalty), blockers }
}

function visible(node: Element): boolean {
  const style = getComputedStyle(node)
  const box = node.getBoundingClientRect()
  return style.display !== "none" && style.visibility !== "hidden" &&
    Number(style.opacity) > 0 && box.width > 0 && box.height > 0
}

function effectivelyVisible(node: Element): boolean {
  const style = getComputedStyle(node)
  if (style.display === "none" || style.visibility === "hidden" || Number(style.opacity) <= 0) {
    return false
  }
  if (visible(node)) return true
  return style.display === "contents" && Array.from(node.querySelectorAll("*")).some(visible)
}

function intentionalOverlapProxy(node: Element): boolean {
  const style = getComputedStyle(node)
  return node.getAttribute("data-cogo-composite-action-proxy") === "true" ||
    node.hasAttribute("data-cogo-parent-activation-proxy") ||
    style.display === "contents" ||
    (style.position === "absolute" && style.pointerEvents === "none")
}

function overlaps(left: DOMRect, right: DOMRect): boolean {
  return Math.min(left.right, right.right) - Math.max(left.left, right.left) > 1 &&
    Math.min(left.bottom, right.bottom) - Math.max(left.top, right.top) > 1
}

function siblingOverlapCount(root: Element): number {
  let count = 0
  const parents = [root, ...Array.from(root.querySelectorAll("*"))]
  for (const parent of parents) {
    if (parent.closest("svg")) continue
    if (parent.getAttribute("data-overlap-contract") === "allowed") continue
    const children = Array.from(parent.children).filter((child) =>
      visible(child) && !intentionalOverlapProxy(child),
    )
    for (let left = 0; left < children.length; left += 1) {
      for (let right = left + 1; right < children.length; right += 1) {
        const leftNode = children[left]!
        const rightNode = children[right]!
        if (
          leftNode.getAttribute("data-overlap-contract") === "allowed" ||
          rightNode.getAttribute("data-overlap-contract") === "allowed"
        ) continue
        if (overlaps(leftNode.getBoundingClientRect(), rightNode.getBoundingClientRect())) count += 1
      }
    }
  }
  return count
}

function clippingCount(root: Element): number {
  return [root, ...Array.from(root.querySelectorAll("*"))].filter((node) => {
    if (!visible(node) || node.getAttribute("data-clipping-contract") === "allowed") return false
    if (node.matches("input,textarea,select")) return false
    const html = node as HTMLElement
    const style = getComputedStyle(node)
    const intentionalTextClip = style.textOverflow === "ellipsis" ||
      (Number.parseInt(style.webkitLineClamp, 10) || 0) > 0
    if (intentionalTextClip) return false
    const clipsX = ["hidden", "clip"].includes(style.overflowX) && html.scrollWidth > html.clientWidth + 1
    const clipsY = ["hidden", "clip"].includes(style.overflowY) && html.scrollHeight > html.clientHeight + 1
    return clipsX || clipsY
  }).length
}

function parseRgb(value: string): Rgb | null {
  const match = /^rgba?\(\s*([\d.]+)[, ]+([\d.]+)[, ]+([\d.]+)(?:\s*[,/]\s*([\d.]+))?\s*\)$/u.exec(value)
  if (!match) return null
  return {
    r: Number(match[1]) / 255,
    g: Number(match[2]) / 255,
    b: Number(match[3]) / 255,
    a: match[4] === undefined ? 1 : Number(match[4]),
  }
}

function oklabToRgb(lightness: number, a: number, b: number, alpha: number): Rgb {
  const lPrime = lightness + 0.3963377774 * a + 0.2158037573 * b
  const mPrime = lightness - 0.1055613458 * a - 0.0638541728 * b
  const sPrime = lightness - 0.0894841775 * a - 1.291485548 * b
  const l = lPrime ** 3
  const m = mPrime ** 3
  const s = sPrime ** 3
  const encodeSrgb = (linear: number): number => {
    const encoded = linear <= 0.0031308
      ? 12.92 * linear
      : 1.055 * linear ** (1 / 2.4) - 0.055
    return Math.max(0, Math.min(1, encoded))
  }
  return {
    r: encodeSrgb(4.0767416621 * l - 3.3077115913 * m + 0.2309699292 * s),
    g: encodeSrgb(-1.2684380046 * l + 2.6097574011 * m - 0.3413193965 * s),
    b: encodeSrgb(-0.0041960863 * l - 0.7034186147 * m + 1.707614701 * s),
    a: alpha,
  }
}

function parseOklab(value: string): Rgb | null {
  const number = "[+-]?(?:\\d+(?:\\.\\d*)?|\\.\\d+)"
  const match = new RegExp(
    `^oklab\\(\\s*(${number})(%?)\\s+(${number})\\s+(${number})(?:\\s*\\/\\s*(${number})(%?))?\\s*\\)$`,
    "u",
  ).exec(value)
  if (!match) return null
  const lightness = Number(match[1]) / (match[2] === "%" ? 100 : 1)
  const alpha = match[5] === undefined ? 1 : Number(match[5]) / (match[6] === "%" ? 100 : 1)
  return oklabToRgb(lightness, Number(match[3]), Number(match[4]), alpha)
}

function parseOklch(value: string): Rgb | null {
  const match = /^oklch\(\s*([\d.]+)(%?)\s+([\d.]+)\s+([\d.]+)(?:\s*\/\s*([\d.]+)(%?))?\s*\)$/u.exec(value)
  if (!match) return null
  const lightness = Number(match[1]) / (match[2] === "%" ? 100 : 1)
  const chroma = Number(match[3])
  const angle = Number(match[4]) * Math.PI / 180
  const a = chroma * Math.cos(angle)
  const b = chroma * Math.sin(angle)
  const alpha = match[5] === undefined ? 1 : Number(match[5]) / (match[6] === "%" ? 100 : 1)
  return oklabToRgb(lightness, a, b, alpha)
}

function color(value: string): Rgb | null {
  if (value === "transparent") return { r: 0, g: 0, b: 0, a: 0 }
  return parseRgb(value) ?? parseOklab(value) ?? parseOklch(value)
}

function relativeLuminance(value: Rgb): number {
  const channel = (entry: number) => entry <= 0.04045
    ? entry / 12.92
    : ((entry + 0.055) / 1.055) ** 2.4
  return 0.2126 * channel(value.r) + 0.7152 * channel(value.g) + 0.0722 * channel(value.b)
}

function contrastRatio(foreground: Rgb, background: Rgb): number {
  const left = relativeLuminance(foreground)
  const right = relativeLuminance(background)
  return (Math.max(left, right) + 0.05) / (Math.min(left, right) + 0.05)
}

function composite(foreground: Rgb, background: Rgb): Rgb {
  const alpha = foreground.a + background.a * (1 - foreground.a)
  if (alpha <= 0) return { r: 0, g: 0, b: 0, a: 0 }
  return {
    r: (foreground.r * foreground.a + background.r * background.a * (1 - foreground.a)) / alpha,
    g: (foreground.g * foreground.a + background.g * background.a * (1 - foreground.a)) / alpha,
    b: (foreground.b * foreground.a + background.b * background.a * (1 - foreground.a)) / alpha,
    a: alpha,
  }
}

function effectiveBackground(node: Element): Rgb | null {
  let accumulated: Rgb = { r: 0, g: 0, b: 0, a: 0 }
  let current: Element | null = node
  while (current) {
    const parsed = color(getComputedStyle(current).backgroundColor)
    if (!parsed) return null
    accumulated = composite(accumulated, parsed)
    if (accumulated.a >= 0.999) return accumulated
    current = current.parentElement
  }
  return composite(accumulated, { r: 1, g: 1, b: 1, a: 1 })
}

function textNodes(root: Element): Element[] {
  return [root, ...Array.from(root.querySelectorAll("*"))].filter((node) =>
    visible(node) && Array.from(node.childNodes).some((child) =>
      child.nodeType === Node.TEXT_NODE && Boolean(child.textContent?.trim()),
    ),
  )
}

function contentNodes(root: Element): Element[] {
  return [root, ...Array.from(root.querySelectorAll("*"))].filter((node) => {
    if (!visible(node)) return false
    const tag = node.tagName.toLowerCase()
    return ["button", "a", "input", "textarea", "select", "img", "svg", "canvas", "video"]
      .includes(tag) || Array.from(node.childNodes).some((child) =>
        child.nodeType === Node.TEXT_NODE && Boolean(child.textContent?.trim()),
      )
  })
}

function contentDensityRatio(root: Element, nodes: readonly Element[]): number {
  if (nodes.length === 0) return 0
  const boxes = [root, ...nodes]
    .map((node) => node.getBoundingClientRect())
    .filter((box) => box.width > 0 && box.height > 0)
  if (boxes.length === 0) return 0
  const bounds = boxes.reduce((result, box) => ({
    left: Math.min(result.left, box.left),
    top: Math.min(result.top, box.top),
    right: Math.max(result.right, box.right),
    bottom: Math.max(result.bottom, box.bottom),
  }), {
    left: Number.POSITIVE_INFINITY,
    top: Number.POSITIVE_INFINITY,
    right: Number.NEGATIVE_INFINITY,
    bottom: Number.NEGATIVE_INFINITY,
  })
  const visualArea = Math.max(0, bounds.right - bounds.left) * Math.max(0, bounds.bottom - bounds.top)
  if (visualArea <= 0) return 0
  const contentArea = nodes.reduce((sum, node) => {
    const box = node.getBoundingClientRect()
    const width = Math.max(0, Math.min(bounds.right, box.right) - Math.max(bounds.left, box.left))
    const height = Math.max(0, Math.min(bounds.bottom, box.bottom) - Math.max(bounds.top, box.top))
    return sum + width * height
  }, 0)
  return Math.min(1, contentArea / visualArea)
}

function contrastFailures(root: Element): { failures: number; invalidColors: number } {
  let failures = 0
  let invalidColors = 0
  for (const node of textNodes(root)) {
    const style = getComputedStyle(node)
    const foreground = color(style.color)
    const background = effectiveBackground(node)
    if (!foreground || !background) {
      invalidColors += 1
      continue
    }
    const fontSize = Number.parseFloat(style.fontSize)
    const fontWeight = Number.parseInt(style.fontWeight, 10) || 400
    const large = fontSize >= 24 || (fontSize >= 18.66 && fontWeight >= 700)
    const effectiveForeground = foreground.a < 0.999
      ? composite(foreground, background)
      : foreground
    if (contrastRatio(effectiveForeground, background) < (large ? 3 : 4.5)) failures += 1
  }
  return { failures, invalidColors }
}

function typographyHierarchyPass(root: Element): boolean {
  const headings = Array.from(root.querySelectorAll("h1,h2,h3,h4,h5,h6")).filter(visible)
  let priorLevel = 0
  let priorSize = Number.POSITIVE_INFINITY
  for (const heading of headings) {
    const level = Number(heading.tagName.slice(1))
    const size = Number.parseFloat(getComputedStyle(heading).fontSize)
    if (priorLevel > 0 && level > priorLevel && size > priorSize + 0.1) return false
    priorLevel = level
    priorSize = size
  }
  return true
}

export function measureRequiredUiBlockQuality(
  root: Element,
  options: RequiredUiBlockQualityDomOptions,
): RequiredUiBlockQualityObservation {
  const contrast = contrastFailures(root)
  const content = contentNodes(root)
  const requiredActions = [...new Set(options.requiredActionElementIds)]
  const visibleRequiredActionCount = requiredActions.filter((elementId) => {
    // Collection renderers make the runtime element ID unique per instance,
    // but ActionFlow remains authored against the stable source element ID.
    // An action is usable when any concrete instance of that source action is
    // visible; checking only the authored ID incorrectly marks repeated
    // controls (toast dismiss, card add-to-cart, row actions) as hidden.
    const selector = `[data-cogo-element-id="${CSS.escape(elementId)}"], ` +
      `[data-cogo-source-element-id="${CSS.escape(elementId)}"]`
    return Array.from(root.querySelectorAll(selector)).some(effectivelyVisible)
  }).length
  const hiddenRequiredActionCount = requiredActions.length > 0 && visibleRequiredActionCount === 0
    ? requiredActions.length
    : 0
  const interactive = Array.from(root.querySelectorAll(
    "button,a[href],input:not([type=hidden]),textarea,select,[role=button],[tabindex]:not([tabindex='-1'])",
  )).filter(visible)
  const mobileTouchTargetFailureCount = options.mobile
    ? interactive.filter((node) => {
        const box = node.getBoundingClientRect()
        return box.width < 44 || box.height < 44
      }).length
    : 0
  const unresolvedCssVariableCount = [root, ...Array.from(root.querySelectorAll("*"))].filter((node) => {
    const style = getComputedStyle(node)
    return [style.color, style.backgroundColor, style.borderColor, style.fontSize, style.borderRadius]
      .some((value) => value.includes("var(") || value.trim() === "")
  }).length
  const unintendedHorizontalOverflow = (root as HTMLElement).scrollWidth > (root as HTMLElement).clientWidth + 1
  return evaluateRequiredUiBlockQuality({
    overlapCount: siblingOverlapCount(root),
    clippingCount: clippingCount(root),
    unintendedHorizontalOverflow,
    hiddenRequiredActionCount,
    unresolvedCssVariableCount,
    invalidComputedColorCount: contrast.invalidColors,
    contrastFailureCount: contrast.failures,
    mobileTouchTargetFailureCount,
    typographyHierarchyPass: typographyHierarchyPass(root),
    emptyVisibleContent: content.length === 0,
    contentDensityRatio: contentDensityRatio(root, content),
    unresolvedExpressionCount: textNodes(root).filter((node) =>
      /#\{[^}]+\}/u.test(node.textContent ?? ""),
    ).length,
  })
}
